import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { 
  CreditCard, Check, X, AlertTriangle, Clock, User, QrCode, 
  Download, Eye, Trash2, RefreshCw, Shield, Calendar, Mail, Phone,
  Image, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { idCardsAPI, workersAPI, filesAPI } from '../../services/api';

// Sanitize HTML helper
const sanitizeHtml = (html) => DOMPurify.sanitize(html);

// Safe HTML escaping to prevent XSS
const escapeHtml = (text) => {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

const IDCardsTab = () => {
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedCard, setSelectedCard] = useState(null);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueNotes, setIssueNotes] = useState('');
  const [selectedWorkerId, setSelectedWorkerId] = useState('');

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [cardsRes, statsRes, workersRes] = await Promise.all([
        idCardsAPI.getAll(filter === 'all' ? null : filter),
        idCardsAPI.getStats(),
        workersAPI.getAll()
      ]);
      setCards(cardsRes.data);
      setStats(statsRes.data);
      setWorkers(workersRes.data);
    } catch (error) {
      toast.error('Failed to load ID cards data');
    } finally {
      setLoading(false);
    }
  };

  const handleIssueCard = async () => {
    if (!selectedWorkerId) {
      toast.error('Please select a worker');
      return;
    }
    
    try {
      await idCardsAPI.issueCard(selectedWorkerId, issueNotes || undefined);
      toast.success('ID card issued successfully');
      setShowIssueModal(false);
      setSelectedWorkerId('');
      setIssueNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue ID card');
    }
  };

  const handleApprove = async (cardId) => {
    try {
      await idCardsAPI.approveCard(cardId);
      toast.success('ID card approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRevoke = async (cardId) => {
    const reason = prompt('Enter revocation reason (optional):');
    try {
      await idCardsAPI.revokeCard(cardId, reason || undefined);
      toast.success('ID card revoked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke');
    }
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this ID card?')) return;
    try {
      await idCardsAPI.deleteCard(cardId);
      toast.success('ID card deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-[var(--warning)]/30',
      approved: 'bg-[var(--success)]/15 text-green-700 border-[var(--success)]/30',
      revoked: 'bg-[var(--error)]/15 text-red-700 border-[var(--error)]/30',
      expired: 'bg-[var(--background-subtle)] text-[var(--text-primary)] border-[var(--border-primary)]'
    };
    const icons = {
      pending: Clock,
      approved: CheckCircle,
      revoked: XCircle,
      expired: AlertCircle
    };
    const Icon = icons[status] || AlertCircle;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.expired}`}>
        <Icon className="w-3 h-3" />
        {status?.toUpperCase()}
      </span>
    );
  };

  // Workers eligible for ID cards (have picture, don't have card yet)
  const eligibleWorkers = workers.filter(w => {
    const hasCard = cards.some(c => c.worker_id === w.id);
    return w.profile_picture && !hasCard;
  });

  // Workers needing pictures
  const workersNeedingPictures = workers.filter(w => !w.profile_picture);

  return (
    <div className="space-y-6" data-testid="id-cards-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <CreditCard className="w-7 h-7 text-[var(--primary)]" />
            Employee ID Cards
          </h2>
          <p className="text-[var(--text-muted)] text-sm">Manage worker identification cards with QR verification</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchData}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button 
            onClick={() => setShowIssueModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="issue-card-btn"
          >
            <CreditCard className="w-4 h-4" />
            Issue New Card
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--primary)]">{stats.total_cards}</p>
            <p className="text-xs text-[var(--text-muted)]">Total Cards</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{stats.approved}</p>
            <p className="text-xs text-[var(--text-muted)]">Active</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            <p className="text-xs text-[var(--text-muted)]">Pending</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--error)]">{stats.revoked}</p>
            <p className="text-xs text-[var(--text-muted)]">Revoked</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#D4AF37]">{stats.workers_without_cards}</p>
            <p className="text-xs text-[var(--text-muted)]">No Card Yet</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{stats.workers_without_pictures}</p>
            <p className="text-xs text-[var(--text-muted)]">Need Photo</p>
          </div>
        </div>
      )}

      {/* Workers Needing Pictures Alert */}
      {workersNeedingPictures.length > 0 && (
        <div className="bg-[var(--warning)]/10 border border-[var(--warning)]/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Image className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-800">Workers Need Profile Pictures</h4>
              <p className="text-sm text-amber-700 mb-2">
                These workers cannot receive ID cards until they upload a profile photo:
              </p>
              <div className="flex flex-wrap gap-2">
                {workersNeedingPictures.map(w => (
                  <span key={w.id} className="px-2 py-1 bg-amber-100 rounded text-xs text-amber-800">
                    {w.full_name} ({w.employee_id || 'No ID'})
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b border-[var(--border-primary)] pb-2">
        {['all', 'pending', 'approved', 'revoked'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              filter === f 
                ? 'bg-[var(--primary)] text-white' 
                : 'text-[var(--text-muted)] hover:bg-[var(--background-subtle)]'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'pending' && stats?.pending > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                {stats.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Cards Grid */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[var(--primary)]" />
          <p className="mt-2 text-[var(--text-muted)]">Loading ID cards...</p>
        </div>
      ) : cards.length === 0 ? (
        <div className="text-center py-12 card">
          <CreditCard className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-[var(--text-secondary)]">No ID Cards Found</h3>
          <p className="text-[var(--text-muted)]">
            {filter === 'all' 
              ? "No ID cards have been issued yet. Click 'Issue New Card' to create one."
              : `No ${filter} ID cards found.`}
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map(card => (
            <div 
              key={card.id} 
              className={`card overflow-hidden ${
                card.status === 'revoked' ? 'opacity-75 border-[var(--error)]/30' : ''
              }`}
              data-testid={`id-card-${card.id}`}
            >
              {/* Card Header - Simulated ID Card Look */}
              <div className="bg-gradient-to-r from-[var(--primary)] to-[#2d4a7c] p-4 text-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    <span className="font-bold text-sm">NC HELP HUB</span>
                  </div>
                  {getStatusBadge(card.status)}
                </div>
                <div className="flex items-center gap-4">
                  {/* Worker Photo */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-[var(--background-paper)]/20 flex-shrink-0">
                    {card.worker_picture ? (
                      <img 
                        src={filesAPI.getUrl(card.worker_picture)} 
                        alt={card.worker_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-10 h-10 text-white/50" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg truncate">{card.worker_name}</h3>
                    <p className="text-white/90 font-mono text-xl">{card.employee_id}</p>
                    <p className="text-white/70 text-xs truncate">
                      {card.service_types?.join(' • ')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-3">
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-[var(--text-muted)]">
                    <Mail className="w-3 h-3" />
                    <span className="truncate text-xs">{card.worker_email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[var(--text-muted)]">
                    <Phone className="w-3 h-3" />
                    <span className="text-xs">{card.worker_phone}</span>
                  </div>
                </div>

                {/* Dates */}
                <div className="flex items-center justify-between text-xs text-[var(--text-muted)] border-t pt-2">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>Issued: {new Date(card.issued_date).toLocaleDateString()}</span>
                  </div>
                  {card.expiration_date && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>Exp: {new Date(card.expiration_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                {/* QR Code */}
                {card.qr_code && card.status === 'approved' && (
                  <div className="flex items-center justify-center pt-2 border-t">
                    <div className="text-center">
                      <img 
                        src={`data:image/png;base64,${card.qr_code}`} 
                        alt="QR Code"
                        className="w-20 h-20 mx-auto"
                      />
                      <p className="text-[10px] text-[var(--text-muted)] mt-1">Scan to verify</p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t">
                  {card.status === 'pending' && (
                    <>
                      <Button 
                        size="sm" 
                        onClick={() => handleApprove(card.id)}
                        className="flex-1 bg-[var(--success)] hover:bg-[var(--success)]/80 text-white"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleRevoke(card.id)}
                        className="flex-1"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  {card.status === 'approved' && (
                    <>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setSelectedCard(card)}
                        className="flex-1"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleRevoke(card.id)}
                        className="text-[var(--error)] border-[var(--error)]/30 hover:bg-[var(--error)]/10"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {card.status === 'revoked' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleDelete(card.id)}
                      className="flex-1 text-[var(--error)] border-[var(--error)]/30 hover:bg-[var(--error)]/10"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Issue Card Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--background-paper)] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6 border-b">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-[var(--primary)]" />
                Issue New ID Card
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {eligibleWorkers.length === 0 ? (
                <div className="text-center py-6">
                  <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
                  <p className="text-[var(--text-muted)]">
                    No eligible workers found. Workers must have a profile picture to receive an ID card.
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Select Worker *</label>
                    <select
                      value={selectedWorkerId}
                      onChange={(e) => setSelectedWorkerId(e.target.value)}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                    >
                      <option value="">Choose a worker...</option>
                      {eligibleWorkers.map(w => (
                        <option key={w.id} value={w.id}>
                          {w.full_name} ({w.employee_id || 'No ID'}) - {w.email}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                    <textarea
                      value={issueNotes}
                      onChange={(e) => setIssueNotes(e.target.value)}
                      placeholder="Any notes about this ID card..."
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                      rows={3}
                    />
                  </div>
                  <div className="bg-[#D4AF37]/10 p-3 rounded-lg text-sm text-[#D4AF37]">
                    <p className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Card will be valid for 1 year from issue date
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className="p-6 border-t flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowIssueModal(false);
                  setSelectedWorkerId('');
                  setIssueNotes('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              {eligibleWorkers.length > 0 && (
                <Button 
                  onClick={handleIssueCard}
                  className="flex-1 btn-primary"
                  disabled={!selectedWorkerId}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Issue Card
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Card Modal */}
      {selectedCard && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--background-paper)] rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            {/* Full ID Card Preview */}
            <div className="bg-gradient-to-br from-[var(--primary)] via-[#2d4a7c] to-[#1a365d] p-8 text-white relative">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--background-paper)]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-[var(--background-paper)]/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-bold">NC HELP HUB</h2>
                    <p className="text-white/70 text-sm">Employee Identification Card</p>
                  </div>
                </div>
                
                <div className="flex gap-6">
                  <div className="w-32 h-40 rounded-xl overflow-hidden bg-[var(--background-paper)]/20 flex-shrink-0 shadow-lg">
                    {selectedCard.worker_picture ? (
                      <img 
                        src={filesAPI.getUrl(selectedCard.worker_picture)} 
                        alt={selectedCard.worker_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-16 h-16 text-white/50" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-1">{selectedCard.worker_name}</h3>
                    <p className="text-3xl font-mono font-bold text-[#4CAF50] mb-3">
                      {selectedCard.employee_id}
                    </p>
                    <div className="space-y-1 text-sm text-white/80">
                      <p>{selectedCard.service_types?.join(' • ')}</p>
                      <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {selectedCard.worker_email}
                      </p>
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {selectedCard.worker_phone}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 bg-[var(--background-paper)]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-[var(--text-muted)]">
                    Issued: {new Date(selectedCard.issued_date).toLocaleDateString()}
                  </p>
                  {selectedCard.expiration_date && (
                    <p className="text-sm text-[var(--text-muted)]">
                      Expires: {new Date(selectedCard.expiration_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {selectedCard.qr_code && (
                  <div className="text-center">
                    <img 
                      src={`data:image/png;base64,${selectedCard.qr_code}`} 
                      alt="QR Code"
                      className="w-24 h-24 border rounded-lg"
                    />
                    <p className="text-[10px] text-[var(--text-muted)] mt-1">Scan to verify</p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedCard(null)}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    // Create printable version with escaped content for XSS protection
                    const printWindow = window.open('', '_blank', 'width=800,height=600');
                    if (!printWindow) return;
                    const safeName = escapeHtml(selectedCard.worker_name);
                    const safeId = escapeHtml(selectedCard.employee_id);
                    const safeServices = escapeHtml(selectedCard.service_types?.join(' • ') || '');
                    const safeDate = escapeHtml(new Date(selectedCard.expiration_date).toLocaleDateString());
                    
                    const htmlContent = `
                          <div class="card" style="width: 3.375in; height: 2.125in; background: linear-gradient(135deg, #1a365d, #2d4a7c); color: white; border-radius: 12px; padding: 16px; box-sizing: border-box; font-family: Arial, sans-serif;">
                            <div class="header" style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                              <div><strong>NC HELP HUB</strong><br/><small style="opacity:0.7">Employee ID</small></div>
                            </div>
                            <div class="content" style="display: flex; gap: 12px;">
                              <div class="photo" style="width: 70px; height: 85px; background: rgba(255,255,255,0.2); border-radius: 8px;"></div>
                              <div class="info">
                                <h3 style="margin: 0 0 4px 0; font-size: 14px;">${safeName}</h3>
                                <div class="id" style="font-family: monospace; font-size: 18px; color: #4CAF50; margin-bottom: 8px;">${safeId}</div>
                                <p style="margin: 2px 0; font-size: 9px; opacity: 0.8;">${safeServices}</p>
                                <p style="margin: 2px 0; font-size: 9px; opacity: 0.8;">Exp: ${safeDate}</p>
                              </div>
                            </div>
                          </div>
                    `;
                    const doc = printWindow.document;
                    const html = doc.createElement('html');
                    const head = doc.createElement('head');
                    const titleEl = doc.createElement('title');
                    titleEl.textContent = 'ID Card';
                    head.appendChild(titleEl);
                    const body = doc.createElement('body');
                    body.style.cssText = 'display:flex;justify-content:center;align-items:center;min-height:100vh;margin:0;background:#f0f0f0;';
                    const container = doc.createElement('div');
                    container.innerHTML = sanitizeHtml(htmlContent);
                    body.appendChild(container);
                    html.appendChild(head);
                    html.appendChild(body);
                    doc.documentElement.replaceWith(html);
                    setTimeout(() => { printWindow.focus(); printWindow.print(); }, 300);
                  }}
                  className="flex-1 btn-primary"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Print Card
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IDCardsTab;
